<script setup>
/**
 * 快三（kuaisan）界面还原预览页 —— 对应旧版 chunk web/27.173ca64c5ebabb1eb4a2.js。
 *
 * 旧版该页的玩法为「点数 / 大小 / 豹子」（bundle 中可见「点数」「大小」「豹」三个文案，
 * 以及 number-da / -xiao / -dan / -shuang 号码样式类），开奖为 3 颗骰子。
 * 后端 games/ 目录下没有 kuaisan 游戏，因此本页不接任何接口，全部为占位数据。
 */
import { computed, ref } from 'vue'
import GamePreviewShell from '../components/GamePreviewShell.vue'

const types = [
  { key: 'point', title: '点数' },
  { key: 'size', title: '大小' },
  { key: 'triple', title: '豹子' },
]
// 三颗骰子和值范围 3-18；占位赔率，接后端后应改为读取 /home/game/odds。
const pointRange = Array.from({ length: 16 }, (_, index) => index + 3)
const pointOdds = {
  3: '207.00', 4: '69.00', 5: '35.00', 6: '21.00', 7: '13.00', 8: '9.00', 9: '7.50', 10: '7.00',
  11: '7.00', 12: '7.50', 13: '9.00', 14: '13.00', 15: '21.00', 16: '35.00', 17: '69.00', 18: '207.00',
}
const sizePlays = [['大', '1.950'], ['小', '1.950'], ['单', '1.950'], ['双', '1.950']]
const placeholderLottery = { period: '20260806215', dice: [4, 6, 3] }
const placeholderHistory = [
  { no: '20260806214', dice: [2, 2, 5] },
  { no: '20260806213', dice: [6, 6, 6] },
  { no: '20260806212', dice: [1, 4, 3] },
]

const activeType = ref('point')
const selected = ref(new Set())

// 快三规则：三颗骰子点数相同为豹子（通杀大小单双），和值 >=11 为大。
function sumOf(dice) { return dice.reduce((total, value) => total + value, 0) }
function isTriple(dice) { return dice.every((value) => value === dice[0]) }
function resultLabel(dice) {
  if (isTriple(dice)) return `豹子 ${dice[0]}`
  const sum = sumOf(dice)
  return `${sum >= 11 ? '大' : '小'}${sum % 2 ? '单' : '双'}`
}
const lotterySum = computed(() => sumOf(placeholderLottery.dice))
const summary = computed(() => `点数 ${lotterySum.value} ${resultLabel(placeholderLottery.dice)}`)

function toggle(key) {
  const next = new Set(selected.value)
  next.has(key) ? next.delete(key) : next.add(key)
  selected.value = next
}
function reset() { selected.value = new Set() }
</script>

<template>
  <GamePreviewShell
    title="快三"
    :period="placeholderLottery.period"
    :countdown="74"
    :total-bet="0"
    :selected-count="selected.size"
    @reset="reset"
  >
    <template #lottery-summary><span class="summary">{{ summary }}</span></template>

    <template #lottery>
      <i v-for="(value, index) in placeholderLottery.dice" :key="`${value}-${index}`" class="dice">{{ value }}</i>
      <b class="equals">=</b>
      <em class="dice-sum">{{ lotterySum }}</em>
    </template>

    <template #history>
      <article v-for="row in placeholderHistory" :key="row.no" class="history-row">
        <span>{{ row.no }}</span>
        <div><i v-for="(value, index) in row.dice" :key="`${row.no}-${index}`" class="dice small">{{ value }}</i></div>
        <b>{{ sumOf(row.dice) }} {{ resultLabel(row.dice) }}</b>
      </article>
    </template>

    <template #types>
      <button
        v-for="type in types"
        :key="type.key"
        type="button"
        :class="['bet-type-item', { 'bet-type-active': activeType === type.key }]"
        @click="activeType = type.key"
      >{{ type.title }}</button>
    </template>

    <template #board>
      <div v-if="activeType === 'point'" class="board-grid grid-4">
        <button
          v-for="value in pointRange"
          :key="value"
          type="button"
          :class="['board-item', { selected: selected.has(`point-${value}`) }]"
          @click="toggle(`point-${value}`)"
        >
          <strong>{{ value }}</strong>
          <small class="odds">{{ pointOdds[value] }}</small>
        </button>
      </div>

      <div v-else-if="activeType === 'size'" class="board-grid grid-4">
        <button
          v-for="[label, odds] in sizePlays"
          :key="label"
          type="button"
          :class="['board-item', { selected: selected.has(`size-${label}`) }]"
          @click="toggle(`size-${label}`)"
        >
          <strong>{{ label }}</strong>
          <small class="odds">{{ odds }}</small>
        </button>
      </div>

      <div v-else class="board-grid grid-3">
        <button
          v-for="value in [1, 2, 3, 4, 5, 6]"
          :key="value"
          type="button"
          :class="['board-item', { selected: selected.has(`triple-${value}`) }]"
          @click="toggle(`triple-${value}`)"
        >
          <span class="triple-dice"><i class="dice small">{{ value }}</i><i class="dice small">{{ value }}</i><i class="dice small">{{ value }}</i></span>
          <small class="odds">207.00</small>
        </button>
      </div>
    </template>
  </GamePreviewShell>
</template>

<style scoped>
.summary{color:#111;font-size:12px}
.dice{display:inline-grid;width:34px;height:34px;place-items:center;border:1px solid #ddd;border-radius:6px;background:#fff;color:#e8353d;font-size:19px;font-style:normal;font-weight:700}
.dice.small{width:20px;height:20px;border-radius:4px;font-size:12px}
.equals{color:#999}
.dice-sum{color:#e6373d;font-size:19px;font-style:normal;font-weight:700}
.history-row{display:grid;grid-template-columns:100px 1fr 76px;min-height:32px;align-items:center;padding:0 11px;border-bottom:1px solid #e5e5e5}
.history-row>div{display:flex;gap:5px}
.history-row b{color:#e6373d;text-align:right;font-weight:600}
.bet-type-item{flex:0 0 auto;border:0;border-right:1px solid #eee;background:#fff;color:#444;padding:9px 16px;font-size:13px;white-space:nowrap}
.bet-type-active{background:#f04141;color:#fff}
.board-grid{display:grid;gap:8px}
.grid-3{grid-template-columns:repeat(3,1fr)}
.grid-4{grid-template-columns:repeat(4,1fr)}
.board-item{display:grid;min-height:48px;place-items:center;gap:3px;border:1px solid #77c697;background:#389b61;color:#fff;padding:6px 2px}
.board-item.selected{border-color:#ffe372;background:#24804e;box-shadow:inset 0 0 0 1px #ffe372}
.board-item strong{font-size:16px;font-weight:600}
.triple-dice{display:flex;gap:3px}
.odds{color:#ffe561;font-size:10px}
</style>
